import { Routes, Route } from "react-router-dom";
import { ChronicleAuthProvider } from "./auth/ChronicleAuthProvider";
import ChronicleProtectedRoute from "./auth/ChronicleProtectedRoute";
import ChronicleLayout from "./ChronicleLayout";
import { ChronicleContextWrapper } from "./components/ChronicleContextWrapper";
import ChronicleAuth from "./pages/ChronicleAuth";
import ChronicleAuthConfirm from "./pages/ChronicleAuthConfirm";
import ChronicleNotFound from "./pages/ChronicleNotFound";
import ChronicleLinkRequired from "./pages/ChronicleLinkRequired";

// Lazy load page components for better performance
import ChronicleHomeDashboard from "./pages/ChronicleHomeDashboard";
import ChronicleGarage from "./pages/ChronicleGarage";
import ChronicleVehicleDetail from "./pages/ChronicleVehicleDetail";
import ChronicleHistory from "./pages/ChronicleHistory";
import ChronicleTicketDetail from "./pages/ChronicleTicketDetail";
import ChronicleRecommendations from "./pages/ChronicleRecommendations";
import ChronicleRecommendationDetail from "./pages/ChronicleRecommendationDetail";
import ChronicleFindings from "./pages/ChronicleFindings";
import ChronicleAccount from "./pages/ChronicleAccount";
import ChronicleShopSelector from "./pages/ChronicleShopSelector";
import ChronicleNotifications from "./pages/ChronicleNotifications";
import ChronicleMessagesInbox from "./pages/ChronicleMessagesInbox";
import ChronicleMessageThread from "./pages/ChronicleMessageThread";
import ChronicleMessageCompose from "./pages/ChronicleMessageCompose";

/**
 * Helper component that wraps content requiring Chronicle context.
 */
const ProtectedWithContext = ({ children }: { children: React.ReactNode }) => (
  <ChronicleProtectedRoute>
    <ChronicleContextWrapper>{children}</ChronicleContextWrapper>
  </ChronicleProtectedRoute>
);

export default function ChronicleRouter() {
  if (import.meta.env.VITE_ENABLE_CHRONICLE !== "true") {
    return <ChronicleNotFound reason="disabled" />;
  }

  return (
    <ChronicleAuthProvider>
      <Routes>
        {/* Public auth routes */}
        <Route path="auth" element={<ChronicleAuth />} />
        <Route path="auth/confirm" element={<ChronicleAuthConfirm />} />
        
        {/* Unlinked account page (protected but no context needed) */}
        <Route path="link-required" element={
          <ChronicleProtectedRoute>
            <ChronicleLinkRequired />
          </ChronicleProtectedRoute>
        } />
        
        {/* Shop selector for multi-shop users (protected but no context needed) */}
        <Route path="select-shop" element={
          <ChronicleProtectedRoute>
            <ChronicleShopSelector />
          </ChronicleProtectedRoute>
        } />

        {/* Protected routes with layout and context */}
        <Route element={<ChronicleLayout />}>
          {/* Home Dashboard */}
          <Route index element={
            <ProtectedWithContext>
              <ChronicleHomeDashboard />
            </ProtectedWithContext>
          } />
          
          {/* Garage (Vehicles) */}
          <Route path="garage" element={
            <ProtectedWithContext>
              <ChronicleGarage />
            </ProtectedWithContext>
          } />
          <Route path="garage/:vehicleId" element={
            <ProtectedWithContext>
              <ChronicleVehicleDetail />
            </ProtectedWithContext>
          } />
          
          {/* History (Tickets) */}
          <Route path="history" element={
            <ProtectedWithContext>
              <ChronicleHistory />
            </ProtectedWithContext>
          } />
          <Route path="tickets/:ticketId" element={
            <ProtectedWithContext>
              <ChronicleTicketDetail />
            </ProtectedWithContext>
          } />
          
          {/* Recommendations */}
          <Route path="recommendations" element={
            <ProtectedWithContext>
              <ChronicleRecommendations />
            </ProtectedWithContext>
          } />
          <Route path="recommendations/:recId" element={
            <ProtectedWithContext>
              <ChronicleRecommendationDetail />
            </ProtectedWithContext>
          } />
          
          {/* DVI Findings */}
          <Route path="findings/:ticketId" element={
            <ProtectedWithContext>
              <ChronicleFindings />
            </ProtectedWithContext>
          } />
          
          {/* Notifications */}
          <Route path="notifications" element={
            <ProtectedWithContext>
              <ChronicleNotifications />
            </ProtectedWithContext>
          } />
          
          {/* Messages */}
          <Route path="messages" element={
            <ProtectedWithContext>
              <ChronicleMessagesInbox />
            </ProtectedWithContext>
          } />
          <Route path="messages/new" element={
            <ProtectedWithContext>
              <ChronicleMessageCompose />
            </ProtectedWithContext>
          } />
          <Route path="messages/:threadId" element={
            <ProtectedWithContext>
              <ChronicleMessageThread />
            </ProtectedWithContext>
          } />
          
          {/* Account */}
          <Route path="account" element={
            <ProtectedWithContext>
              <ChronicleAccount />
            </ProtectedWithContext>
          } />
          
          {/* Catch-all */}
          <Route path="*" element={<ChronicleNotFound reason="not-found" />} />
        </Route>
      </Routes>
    </ChronicleAuthProvider>
  );
}
